import React, { Suspense, useRef } from 'react';
import {
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import useScrollToTopOnRender from '../../hooks/useScrollToTopOnRender';
import { Routes } from '../../routing/routes';
import TrackedRoute from '../../routing/TrackedRoute';
import {
  CONTENT_BODY_ID,
  ContentBody,
  ContentContainer,
} from '../../styling/Grid';
import Landing from '../landing';
import SummitViewPage from '../mountains/detail/summitView';
import SearchPlaces from '../places';
import ErrorComponent from '../sharedComponents/ErrorComponent';
import LoadingSuspense from '../sharedComponents/LoadingSuspense';
import Header from './contentHeader';

const CampsiteDetailPage = React.lazy(() => import('../campsites/detail'));
const MountainDetailPage = React.lazy(() => import('../mountains/detail'));
const PeakListDetailPage = React.lazy(() => import('../peakLists/detail'));
const AutoRoutePage = React.lazy(() => import('../routes/auto'));
const PlanRoutePage = React.lazy(() => import('../routes/plans'));
const SavedRoutes = React.lazy(() => import('../routes/saved'));
const TrailDetailPage = React.lazy(() => import('../trails/detail'));
const TrailheadDetailPage = React.lazy(() => import('../trails/trailhead'));
const ViewpointDetailPage = React.lazy(() => import('../viewpoints/detail'));
const WaterpointDetailPage = React.lazy(() => import('../waterpoints/detail'));
const ParkDetailPage = React.lazy(() => import('../parks/detail'));
const GeoLocationPage = React.lazy(() => import('../geoLocation/detail'));
const OfflineMaps = React.lazy(() => import('../offline/offlineMaps'));

const About = React.lazy(() => import('../about'));
const YourStats = React.lazy(() => import('../stats'));
const UserProfile = React.lazy(() => import('../users/detail'));
const UserSettings = React.lazy(() => import('../users/settings'));
const PageNotFound = React.lazy(() => import('../sharedComponents/404'));
const CreatePeakList = React.lazy(() => import('../peakLists/create'));
const CreateMountain = React.lazy(() => import('../mountains/create'));
const CreateCampsite = React.lazy(() => import('../campsites/create'));
const CreateWaterpoint = React.lazy(() => import('../waterpoints/create'));
const CreateViewpoint = React.lazy(() => import('../viewpoints/create'));
const EditTrail = React.lazy(() => import('../trails/create/basic'));
const EditTrailParent = React.lazy(() => import('../trails/create/parent'));
const ComparePeakListPage = React.lazy(() => import('../peakLists/compare'));
const AddTripReport = React.lazy(() => import('../tripReports/form'));
const UploadTripReports = React.lazy(() => import('../tripReports/gpxUploads'));
const TripReportDetail = React.lazy(() => import('../tripReports/detail'));
const AdminPanel = React.lazy(() => import('../admin'));

const ContentRoutes = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useScrollToTopOnRender(containerRef.current);
  return (
    <ContentBody ref={containerRef} id={CONTENT_BODY_ID}>
      <ErrorComponent>
        <Suspense fallback={<LoadingSuspense />}>
          <Switch>
            <TrackedRoute exact path={Routes.Landing} component={null} />
            <TrackedRoute exact path={Routes.Saved} component={SearchPlaces} />
            <TrackedRoute exact path={Routes.SearchMountains} component={SearchPlaces} />
            <TrackedRoute exact path={Routes.SearchCampsites} component={SearchPlaces} />
            <TrackedRoute exact path={Routes.SearchTrails} component={SearchPlaces} />
            <TrackedRoute exact path={Routes.SearchViewpoints} component={SearchPlaces} />
            <TrackedRoute exact path={Routes.SearchParks} component={SearchPlaces} />
            <TrackedRoute exact path={Routes.MountainDetail} component={MountainDetailPage} />
            <TrackedRoute exact path={Routes.CampsiteDetail} component={CampsiteDetailPage} />
            <TrackedRoute exact path={Routes.TrailDetail} component={TrailDetailPage} />
            <TrackedRoute exact path={Routes.TrailheadDetail} component={TrailheadDetailPage} />
            <TrackedRoute exact path={Routes.ViewpointDetail} component={ViewpointDetailPage} />
            <TrackedRoute exact path={Routes.WaterpointDetail} component={WaterpointDetailPage} />
            <TrackedRoute exact path={Routes.ParkDetail} component={ParkDetailPage} />
            <TrackedRoute exact path={Routes.GeoLocationDetail} component={GeoLocationPage} />
            <TrackedRoute exact path={Routes.ListDetail} component={PeakListDetailPage} />
            <TrackedRoute exact path={Routes.UserProfile} component={UserProfile} />
            <TrackedRoute exact path={Routes.UserSettings} component={UserSettings} />
            <TrackedRoute exact path={Routes.OtherUserPeakList} component={ComparePeakListPage} />
            <TrackedRoute exact path={Routes.SavedRoutePlans} component={SavedRoutes} />
            <TrackedRoute exact path={Routes.RoutePlan} component={PlanRoutePage} />
            <TrackedRoute exact path={Routes.TripReportDetail} component={TripReportDetail} />
            <TrackedRoute exact path={Routes.AddTripReport} component={AddTripReport} />
            <TrackedRoute exact path={Routes.EditTripReport} component={AddTripReport} />
            <TrackedRoute exact path={Routes.UploadTripReports} component={UploadTripReports} />
            <TrackedRoute exact path={Routes.CreateMountain} component={CreateMountain} />
            <TrackedRoute exact path={Routes.EditMountain} component={CreateMountain} />
            <TrackedRoute exact path={Routes.CreateCampsite} component={CreateCampsite} />
            <TrackedRoute exact path={Routes.EditCampsite} component={CreateCampsite} />
            <TrackedRoute exact path={Routes.CreateWaterpoint} component={CreateWaterpoint} />
            <TrackedRoute exact path={Routes.EditWaterpoint} component={CreateWaterpoint} />
            <TrackedRoute exact path={Routes.CreateViewpoint} component={CreateViewpoint} />
            <TrackedRoute exact path={Routes.EditViewpoint} component={CreateViewpoint} />
            <TrackedRoute exact path={Routes.EditTrail} component={EditTrail} />
            <TrackedRoute exact path={Routes.EditTrailParent} component={EditTrailParent} />
            <TrackedRoute exact path={Routes.CreateList} component={CreatePeakList} />
            <TrackedRoute exact path={Routes.EditList} component={CreatePeakList} />
            <TrackedRoute exact path={Routes.YourTrips} component={YourStats} />
            <TrackedRoute exact path={Routes.YourProgress} component={YourStats} />
            <TrackedRoute exact path={Routes.YourTotals} component={YourStats} />
            <TrackedRoute exact path={Routes.OfflineMaps} component={OfflineMaps} />
            <TrackedRoute exact path={Routes.About} component={About} />
            <TrackedRoute exact path={Routes.PrivacyPolicy} component={About} />
            <TrackedRoute exact path={Routes.TermsOfUse} component={About} />
            <TrackedRoute exact path={Routes.DataDisclaimer} component={About} />
            <TrackedRoute exact path={Routes.Contact} component={About} />
            <TrackedRoute exact path={Routes.OsmData} component={About} />
            <TrackedRoute path={Routes.AutoRouteDetail} component={AutoRoutePage} />
            {/* Redirects */}
            <Redirect from={'/your-data'} to={Routes.YourTrips} />
            {/* 404 Route -> */}
            <Route exact path={Routes.Admin} component={AdminPanel} />
            <Route component={PageNotFound} />
          </Switch>
        </Suspense>
      </ErrorComponent>
    </ContentBody>
  );
};

const MainContent = () => {
  return (
    <ContentContainer>
      <Header />
      <Switch>
        <TrackedRoute exact path={Routes.Landing} component={Landing} />
        <TrackedRoute exact path={Routes.SummitView} component={SummitViewPage} />
        <Route component={ContentRoutes} />
      </Switch>
    </ContentContainer>
  );
};

export default MainContent;
